import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import CooperationTaskModel from "../../../../model/cooperationTask.model";
import CooperationModel from "../../../../model/cooperation.model";
import UserModel from "../../../../model/user.model";
import CustomerModel from "../../../../model/customer.model";
import NoteModel from "../../../../model/note.model";
import {BehaviorSubject, forkJoin, of, Subject} from "rxjs";
import CooperationService from "../../../../api/cooperation/cooperation.service";
import {MatDialog} from "@angular/material/dialog";
import UserService from "../../../../api/user/user.service";
import CustomerService from "../../../../api/customer/customer.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {debounceTime, distinctUntilChanged, switchMap} from "rxjs/operators";
import * as moment from "moment";
import {CustomerFormComponent, CustomerFormDialogModel} from "../../customer/customer-form/customer-form.component";

@Component({
  selector: 'app-task-form',
  templateUrl: './task-form.component.html',
  styleUrls: ['./task-form.component.scss']
})
export class TaskFormComponent implements OnInit, AfterViewInit {
  public taskText = '';
  public cooperation: CooperationModel;
  public user: UserModel;
  public customer: CustomerModel;
  public taskDue: Date;

  public searchOpened = false;

  @Input() cooperationSearchEnabled = true;
  @Input() userSearchEnabled = true;
  @Input() customerSearchEnabled = true;

  @Input() customerPreset: CustomerModel;
  @Input() cooperationPreset: CooperationModel;
  @Input() userPreset: UserModel;
  @Input() task: CooperationTaskModel;

  @Input() tagTrigger = '#';

  @Input() placeholderText = 'Aufgabe hinzufügen - ' + 'Drücke "' + this.tagTrigger + '" um zu taggen';

  private querySubject = new BehaviorSubject<string>('');
  public query$ = this.querySubject.asObservable();

  private resultsSubject = new BehaviorSubject<QueryResult[]>([]);
  public results$ = this.resultsSubject.asObservable();

  private cooperationsSubject = new BehaviorSubject<CooperationModel[]>([]);
  public cooperations$ = this.cooperationsSubject.asObservable();

  private usersSubject = new BehaviorSubject<UserModel[]>([]);
  public users$ = this.usersSubject.asObservable();

  private customersSubject = new BehaviorSubject<CustomerModel[]>([]);
  public customers$ = this.customersSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private focusSubject = new Subject();
  public focus$ = this.focusSubject.asObservable();

  @ViewChild('taskInput') taskInput: ElementRef;

  @Output() submit = new EventEmitter<NoteModel>();

  public selectedIndex = 0;

  constructor(private cooperationService: CooperationService,
              private dialog: MatDialog,
              private userService: UserService,
              private customerService: CustomerService,
              private snackBar: MatSnackBar) {

  }

  public focus() {
    this.taskInput.nativeElement.focus();
  }

  ngAfterViewInit() {
    if (this.task) {
      this.taskText = this.task.text;
      this.cooperation = this.task.cooperation;
      this.user = this.task.markedUser;
      this.customer = this.task.customer;
    }
  }

  ngOnInit(): void {
    this.focus$.pipe(
        debounceTime(200),
        distinctUntilChanged()
    ).subscribe(focus => {
      if (!focus) {
        this.focusLost();
      }
    });

    this.query$.pipe(
        debounceTime(200),
        distinctUntilChanged()
    ).subscribe(query => {
      if (!this.searchOpened) { return; }
      const requests = [];

      if (this.userSearchEnabled) {
        requests.push(this.userService.list(query, 1, 3)
            .pipe(switchMap(result => {
              const resultList = [] as QueryResult[];
              for (const user of result.results) {
                resultList.push({
                  id: user.id,
                  originalObject: user,
                  label: user.displayName ? user.displayName : user.username,
                  type: 'user',
                });
              }
              return of(resultList);
            })));
      }

      if (this.customerSearchEnabled) {
        requests.push(this.customerService.list(query, 1, 3, null, null, { state: 'customer' })
            .pipe(switchMap(result => {
              const resultList = [] as QueryResult[];
              for (const customer of result.results) {
                resultList.push({
                  id: customer.id,
                  label: customer.companyDisplayName ? customer.companyDisplayName : customer.companyName,
                  type: 'customer',
                  originalObject: customer,
                  image: customer.logo ? customer.logo.filename : null
                });
              }
              return of(resultList);
            })));
      }

      if (this.cooperationSearchEnabled) {
        let cooperationAdditionalParameters = {};
        if(this.user) {
          cooperationAdditionalParameters = {
            user: this.user.id
          };
        }
        requests.push(
            this.cooperationService.list(query, 1, 3, 'created', 'desc',  cooperationAdditionalParameters)
                .pipe(switchMap(result => {
                  const resultList = [] as QueryResult[];
                  for (const coop of result.results) {
                    const dateframe = [];
                    if(coop.earliestEvent) {
                      dateframe.push(moment(coop.earliestEvent).format('DD.MM.YYYY'));
                    }
                    if(coop.latestEvent) {
                      dateframe.push(moment(coop.latestEvent).format('DD.MM.YYYY'));
                    }
                    resultList.push({
                      id: coop.id,
                      originalObject: coop,
                      label: coop.name,
                      image: coop.customer.logo ? coop.customer.logo.filename : null,
                      type: 'cooperation',
                      description: dateframe.join(' - ')
                    });
                  }
                  return of(resultList);
                }))
        );
      }

      forkJoin(requests).subscribe(results => {
        let combinedResults = [];
        for (const result of results) {
          combinedResults = combinedResults.concat(result);
        }
        this.resultsSubject.next(combinedResults);
      });
    });
  }

  keyDown(event) {
    event.stopPropagation();
    if (event.key === this.tagTrigger && !this.searchOpened ) {
      event.preventDefault();
      this.searchOpened = true;
      this.querySubject.next('');
      this.selectedIndex = 0;
    } else if (event.key === this.tagTrigger && this.searchOpened) {
      this.searchOpened = false;
    } else {
      /* handle keypresses inside of a search window */
      if (this.searchOpened) {
        let q = this.querySubject.getValue();
        if (event.key === 'Backspace') {
          q = q.substr(0, q.length - 1);
        } else if (event.key === 'ArrowUp') {
          if (this.selectedIndex > 0) {
            this.selectedIndex--;
          }
        } else if (event.key === 'ArrowDown') {
          if (this.searchOpened && this.resultsSubject.getValue().length > this.selectedIndex + 1) {
            this.selectedIndex++;
          }
        } else if (event.key === 'Enter') {
          this.saveSelection();
          event.preventDefault();
          return;
        } else if (event.code === 'Escape') {
          this.searchOpened = false;
        } else if (event.key.length === 1) {
          q = q + event.key;
        }

        this.querySubject.next(q);
        event.preventDefault();
      }
    }

    if (!this.searchOpened) {
      if (event.key === 'Enter') {
        const coopCopy = JSON.parse(JSON.stringify(this.cooperation ? this.cooperation : null));
        const userCopy = JSON.parse(JSON.stringify(this.user ? this.user : null));
        const customerCopy = JSON.parse(JSON.stringify(this.customer ? this.customer : null));

        const note = {
          text: this.taskInput.nativeElement.value,
          cooperation: coopCopy,
          marked_user: userCopy,
          customer: customerCopy
        };
        this.submit.emit(note);
        this.user = null;
        this.taskInput.nativeElement.value = '';
        this.cooperation = null;
        this.customer = null;
      }
    }
  }

  focusLost() {
    this.searchOpened = false;
  }

  blur(event) {
    this.focusSubject.next(false);
  }

  onFocus(event) {
    this.focusSubject.next(true);
  }

  matchInType(type: string) {
    for(let result of this.resultsSubject.getValue()) {
      if(result.type == type) {
        return true;
      }
    }
    return false;
  }

  saveSelection() {
    if (this.searchOpened) {
      const entries = this.resultsSubject.getValue();
      if (0 <= this.selectedIndex && this.selectedIndex < entries.length) {
        const entry = entries[this.selectedIndex];
        if (entry.type === 'customer') {
          this.customer = entry.originalObject;
        } else if (entry.type === 'user') {
          this.user = entry.originalObject;
        } else if (entry.type === 'cooperation') {
          this.cooperation = entry.originalObject;
        }
      }
    }
    this.searchOpened = false;
  }

  addCustomer() {
    const dialogData = new CustomerFormDialogModel({
      companyName: this.querySubject.getValue(),
      companyDisplayName: this.querySubject.getValue(),
      socialMedia: [],
    } as CustomerModel);

    const dialogRef = this.dialog.open(CustomerFormComponent, {
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.customerService.create(dialogResult).subscribe(result => {
          this.snackBar.open('Unternehmen wurde hinzugefügt');
          this.customer = result;
          this.searchOpened = false;
        }, error => {
          this.snackBar.open('Fehler beim Hinzufügen des Unternehmens');
        });
      }
    });
  }
}

interface QueryResult {
  id: number;
  type: 'customer'|'cooperation'|'user';
  label: string;
  description?: string;
  image?: string;
  originalObject: any;
}
